'use client'

import { useEffect, useMemo, useState } from 'react'
import { useAuth } from '@/hooks/use-auth'

type Player = {
  key?: string
  name: string
  userId?: string
  avatarFileId?: string | null
  races: number
  points: number
  avg: number
  totalRaceTimeSec?: number
  mmr: number
  bestDriver: string
  bestDriverCount: number
  rankId?: string
  rankName?: string
  rankDivision?: string
  rankLabel?: string
  bestBody?: string
  bestBodyCount?: number
  bestTire?: string
  bestWing?: string
}

type RankTier = {
  rankId: string
  name: string
  minMmr: number
  maxMmr: number
  order: number
  divisions: number
}

type Summary = {
  currentSeason?: { seasonId: string; name: string; status: string }
  rankTiers?: RankTier[]
  players: Player[]
  maps: any[]
  drivers: any[]
  historyByPlayer?: Record<string, Array<{ date: string; mmr: number }>>
  recentByPlayer?: Record<string, Array<{ track: string; pos: number; mmrChange: number; time: string; driver?: string; body?: string }>>
  meta: { races: number; players: number; points: number }
}

function norm(s = '') {
  return s.toLowerCase().trim().replace(/\s+/g, ' ')
}

export function useMk8Player(profileId?: string) {
  const { currentUser } = useAuth()
  const [summary, setSummary] = useState<Summary | null>(null)

  useEffect(() => {
    let mounted = true

    const load = () => {
      fetch('/mk8-summary')
        .then((r) => r.json())
        .then((data) => {
          if (mounted) setSummary(data)
        })
        .catch(() => {
          if (mounted) setSummary(null)
        })
    }

    load()
    const timer = setInterval(load, 15000) // refresh every 15s

    return () => {
      mounted = false
      clearInterval(timer)
    }
  }, [])

  const linkedName =
    currentUser?.name?.trim() || currentUser?.email?.split('@')[0] || ''
  const linkedUserId = String((currentUser as any)?.prefs?.userId || (currentUser as any)?.prefs?.emulatorUserId || '').trim()
  const targetProfileId = String(profileId || '').trim()

  const players = summary?.players || []

  const myStats = useMemo(() => {
    if (targetProfileId) {
      const wanted = targetProfileId.toLowerCase()
      return (
        players.find((p) => {
          const a = String((p as any).userId || '').trim().toLowerCase()
          const b = String((p as any).key || '').trim().toLowerCase()
          return a === wanted || b === wanted
        }) || null
      )
    }

    if (linkedUserId) {
      const byId = players.find(
        (p) => String(p.userId || '').trim().toLowerCase() === linkedUserId.toLowerCase(),
      )
      if (byId) return byId
    }

    return players.find((p) => norm(p.name) === norm(linkedName)) || null
  }, [players, linkedName, linkedUserId, targetProfileId])

  const rank = useMemo(() => {
    if (!myStats) return -1
    return players.findIndex((p) => p === myStats)
  }, [players, myStats])

  const historyKey = Object.keys(summary?.historyByPlayer || {}).find(
    (k) =>
      k === (myStats as any)?.key ||
      (linkedUserId && k === linkedUserId) ||
      norm(k) === norm(myStats?.name || linkedName),
  )
  const recentKey = Object.keys(summary?.recentByPlayer || {}).find(
    (k) =>
      k === (myStats as any)?.key ||
      (linkedUserId && k === linkedUserId) ||
      norm(k) === norm(myStats?.name || linkedName),
  )

  const myHistory = historyKey ? summary?.historyByPlayer?.[historyKey] || [] : []
  const myRecentRaces = recentKey ? summary?.recentByPlayer?.[recentKey] || [] : []

  const currentProfileId = String((myStats as any)?.userId || (myStats as any)?.key || '').trim()
  const isOwnProfile = !targetProfileId || (linkedUserId && targetProfileId === linkedUserId)

  return {
    currentUser,
    summary,
    linkedName,
    linkedUserId,
    currentProfileId,
    isOwnProfile,
    rank,
    myStats,
    myHistory,
    myRecentRaces,
  }
}
